import React, { useCallback, useMemo, useId } from 'react';
import { TextField } from '@mui/material';
import FieldLabel from './FieldLabel';
import { ILabel } from '../constants/interfaces';
import {
  getBaseTextFieldStyles,
  getAccessibilityProps,
  getFormHelperTextSlotProps,
  getFieldName,
  getComputedFieldValues,
} from '../styles/fieldStyles';

/**
 * Props for the InputField component
 */
export interface IInputFieldProps {
  /** Current input value */
  value: string;
  /** Callback fired when the input value changes */
  onChange: (value: string) => void;
  /** Field configuration containing label text, validation rules, etc. */
  field: ILabel;
  /** Error message to display */
  errorText?: string;
  /** Whether to hide the label */
  isLabelHidden?: boolean;
  /** Whether the field is read-only/display only */
  displayOnly?: boolean;
  /** Width of the input field */
  width?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Maximum character length */
  maxLength?: number;
  /** Auto-complete hint for the input */
  autoComplete?: string;
  /** Callback fired when input gains focus */
  onFocus?: () => void;
  /** Callback fired when input loses focus */
  onBlur?: () => void;
}

/**
 * Utility functions for input handling
 */
const inputUtils = {
  /**
   * Gets appropriate input type for Material-UI TextField
   */
  getInputType: (fieldType: string): string => {
    const typeMap: Record<string, string> = {
      email: 'email',
      password: 'password',
      search: 'search',
      tel: 'tel',
      text: 'text',
      url: 'url',
    };
    return typeMap[fieldType] || 'text';
  },

  /**
   * Sanitizes input value based on field type
   */
  sanitizeValue: (value: string, fieldType: string): string => {
    switch (fieldType) {
      case 'email':
        return value.toLowerCase().trim();
      case 'text':
      default:
        return value;
    }
  },

  /**
   * Validates input value based on field requirements
   */
  validateInput: (
    value: string,
    field: ILabel,
    maxLength: number
  ): string | null => {
    if (field.isMandatory && !value.trim()) {
      return `${field.text} is required`;
    }

    if (value.length > maxLength) {
      return `${field.text} must be ${maxLength} characters or less`;
    }

    return null;
  },
};

/**
 * InputField Component
 *
 * A reusable text input component that integrates with form validation
 * and provides a consistent interface for text input across the application.
 *
 * Features:
 * - Comprehensive input validation and error handling
 * - Accessibility support with labels and ARIA attributes
 * - Consistent styling with the design system
 * - Support for various input types (text, email, password, etc.)
 * - Memoized performance optimizations
 * - Auto-sanitization based on field type
 *
 * @example
 * ```tsx
 * <InputField
 *   value={formData.name}
 *   onChange={handleNameChange}
 *   field={nameField}
 *   errorText={validationError}
 *   placeholder="Enter your name"
 * />
 * ```
 */
function InputField(props: IInputFieldProps) {
  const {
    value,
    onChange,
    field,
    errorText,
    isLabelHidden = false,
    displayOnly = false,
    width,
    disabled = false,
    readOnly = false,
    placeholder,
    maxLength = 255,
    autoComplete,
    onFocus,
    onBlur,
  } = props;

  // Generate unique ID for accessibility
  const fieldId = useId();
  const labelId = `${fieldId}-label`;
  const errorId = `${fieldId}-error`;

  // Memoize input type to avoid recalculation
  const inputType = useMemo(() => {
    return inputUtils.getInputType(field.type);
  }, [field.type]);

  // Memoize computed values using centralized utility
  const computedValues = useMemo(
    () =>
      getComputedFieldValues(
        errorText,
        disabled,
        displayOnly,
        width,
        maxLength
      ),
    [errorText, disabled, displayOnly, width, maxLength]
  );

  // Optimized change handler with useCallback to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      const sanitizedValue = inputUtils.sanitizeValue(newValue, field.type);
      onChange(sanitizedValue);
    },
    [onChange, field.type]
  );

  // Optimized focus handler
  const handleFocus = useCallback(() => {
    onFocus?.();
  }, [onFocus]);

  // Optimized blur handler
  const handleBlur = useCallback(() => {
    onBlur?.();
  }, [onBlur]);

  return (
    <>
      {/* Label with proper accessibility attributes */}
      <FieldLabel field={field} htmlFor={fieldId} isHidden={isLabelHidden} />

      {/* Input field with enhanced props */}
      <TextField
        id={fieldId}
        fullWidth={String(computedValues.fieldWidth) === '100%'}
        name={getFieldName(field)}
        error={computedValues.hasError}
        size="small"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={computedValues.isDisabled}
        helperText={computedValues.helperText}
        type={inputType}
        placeholder={placeholder}
        autoComplete={autoComplete}
        sx={getBaseTextFieldStyles(
          typeof computedValues.fieldWidth === 'number'
            ? String(computedValues.fieldWidth)
            : computedValues.fieldWidth
        )}
        inputProps={{
          maxLength: computedValues.effectiveMaxLength,
          ...getAccessibilityProps(
            fieldId,
            labelId,
            errorId,
            field,
            isLabelHidden,
            computedValues.hasError
          ),
          readOnly: readOnly,
        }}
        slotProps={getFormHelperTextSlotProps(computedValues.hasError, errorId)}
      />
    </>
  );
}

export default InputField;

// Export utilities for testing and external use
export { inputUtils };
